/* jshint esversion: 6 */
import React, { useEffect, useRef } from 'react';
import { compose } from 'redux';
import Content from 'app/components/common/Content.jsx';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import ComponentWithRouter from 'app/components/Routers/ComponentWithRouter.jsx';
import SocialListModule from 'app/mixins/SocialListModule.js';
import makeToast from 'app/modules/makeToast.js';
import LinkpageProvider from 'app/redux/Providers/LinkpageProvider.jsx';
import * as constants from 'app/redux/constants.js';
import { linkpageErrorMessages } from 'app/utility/LinkpageSettings.js';
import { networkFromServiceCode } from '@/lib/network-service-codes';
import { NETWORKS } from '@/lib/networks';
import LinkpageSettingsScreen from '@/shadcn/components/linkpage/LinkpageSettings.tsx';

const ERROR_KEYS = [constants.LINKPAGE_SETTINGS_LIST, constants.LINKPAGE_SETTINGS_UPDATE];

function networkLabel(psID) {
    const slug = networkFromServiceCode(psID);
    return slug ? NETWORKS[slug].label : '';
}

// "Instagram and X", from what the backend says has a link page.
function supportedNetworks(siteConstants) {
    const labels = (siteConstants.LINKPAGE_SERVICES || []).map(networkLabel).filter(Boolean);
    return labels.length > 1 ? labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1] : labels.join('');
}

function lastError(errors) {
    return errors && errors.length ? errors[errors.length - 1] : null;
}

/*
 * Route container for /config/socials/detail/:socialId/linkpage. Owns everything the screen
 * deliberately does not: the record, the request state, the messages, and where
 * each button goes.
 */
function LinkpageSettings(props) {
    const socialId = props.router.params.socialId;
    const siteConstants = props.app.config.constants;
    const socialsLoaded = props.app.firstLoad[constants.SOCIAL_LIST];
    const social = props.socials.list.find((item) => String(item.id) === String(socialId));
    const supported = social !== undefined && SocialListModule.hasLinkpage(social.psID, siteConstants);
    const settings = props.linkpages[socialId];
    const saving = props.app.loading[constants.LINKPAGE_SETTINGS_UPDATE];
    const listError = lastError(props.getApiErrors([constants.LINKPAGE_SETTINGS_LIST]));
    const updateError = lastError(props.getApiErrors([constants.LINKPAGE_SETTINGS_UPDATE]));
    const wasSaving = useRef(false);

    const fetchSettings = () => props.fetchLinkpageSettings(socialId);

    const goToOutputs = () => {
        props.router.navigate(social ? '/config/socials/detail/' + socialId : '/config/socials');
    };

    useEffect(() => {
        if (supported) {
            fetchSettings();
        }
    }, [socialId, supported]);

    // Errors outlive the screen in app.errors.api, so they would greet the next visit.
    useEffect(() => () => props.deleteApiError(ERROR_KEYS), []);

    // A save that lands goes back to the output it belongs to; one that fails stays, with its errors.
    useEffect(() => {
        if (wasSaving.current && !saving && !updateError) {
            makeToast('Link page settings saved', 'success');
            goToOutputs();
        }
        wasSaving.current = saving;
    }, [saving, updateError]);

    let loadError = null,
        onRetry = undefined;

    if (socialsLoaded && social === undefined) {
        loadError = "We couldn't find this output. It may have been removed.";
    } else if (social !== undefined && !supported) {
        loadError = `Link pages are available for ${supportedNetworks(siteConstants)} outputs.`;
    } else if (listError) {
        loadError = linkpageErrorMessages(listError, "We couldn't load your link page settings. Try again in a moment.").formError;
        onRetry = fetchSettings;
    }

    const {formError, fieldErrors} = linkpageErrorMessages(
        updateError,
        "We couldn't save your link page settings. Try again in a moment."
    );

    const copyLink = (url) => {
        const failed = () => makeToast("Couldn't copy the link", 'error', {description: 'Select it and copy it by hand.'});

        if (!navigator.clipboard) {
            failed();
            return;
        }
        navigator.clipboard.writeText(url).then(() => makeToast('Link copied', 'success'), failed);
    };

    // The owner's view of the page, as the Outputs Link Page button used to open it.
    const editPage = () => window.open('/linkpage/' + socialId, '_blank');

    const main = (
        <div className="tw-root">
            <LinkpageSettingsScreen
                accountName={social ? social.name : ''}
                networkLabel={social ? networkLabel(social.psID) : ''}
                pathSegment={social ? (siteConstants.LINKPAGE_SEGMENTS || {})[social.psID] : undefined}
                settings={settings}
                loading={!loadError && (!socialsLoaded || settings === undefined)}
                saving={saving}
                loadError={loadError}
                onRetry={onRetry}
                formError={formError}
                fieldErrors={fieldErrors}
                onBack={goToOutputs}
                onCancel={goToOutputs}
                onSave={(changes) => props.updateLinkpageSettings(socialId, changes)}
                onCopyLink={copyLink}
                onEditPage={editPage}/>
        </div>
    );

    return (
        <Content header={null} headerClass="white" main={main}/>
    );
}

LinkpageSettings.displayName = "LinkpageSettings";

const enhance = compose(
    ComponentWithContext,
    ComponentWithRouter,
    LinkpageProvider
);

export default enhance(LinkpageSettings);
